import React, { useEffect, useMemo, useState } from 'react';
import './Dashboard.scss';
import { Box, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { getDays } from '../../core/API/organizationSettings';
import { getMyOutstandingTodos } from '../../core/API/todo';
import { getChecklistTemplateById, getMyOutstandingChecklists } from '../../core/API/checklist';
import { getBroadcastMessage } from '../../core/API/broadcast';
import { usePermissionCheck } from '../../core/hooks/usePermissionCheck';
import { useUserAssignmentGroupIds } from '../../core/hooks/useUserAssignmentGroupIds';
import { useWorkplaceUser } from '../../module/documents/utils';
import { useUserData } from '../../store/useUserData';
import { isChecklistEventAssignedToCurrentUser } from '../../core/utils/checklistEventAssignment';
import { sortChecklistEventsByScheduledDateAsc } from '../../core/utils/checklistEventSort';
import { filterTodosForMyScope } from '../../core/utils/todoAssignment';
import { normalizeAndSortTodos, type TodoTableRow } from '../../core/utils/todoScheduleEvent';
import {
	SecurityRolePermission,
} from 'core/services/permissionService';
import { filterBroadcastInboxForUser } from '../../core/utils/broadcastInbox';
import {
	getCurrentUserId,
	resolveEmployeeWidgetVisibility,
} from './utils/homePage';
import {
	fetchMyPublishedShifts,
	resolveStartOfWeekLabel,
} from './utils/scheduleWidget';
import { getBroadcastMessageDate } from './utils/homePageDateFilters';
import EmployeeChecklistWidget, {
	type EmployeeChecklistItem,
} from './widgets/EmployeeChecklistWidget';
import EmployeeTodoWidget from './widgets/EmployeeTodoWidget';
import EmployeeBroadcastWidget from './widgets/EmployeeBroadcastWidget';
import EmployeeScheduleWidget from './widgets/EmployeeScheduleWidget';
import OutstandingChecklistView, {
	type OutstandingChecklistScheduleEvent,
} from '../checklist/components/OutstandingChecklistView';
import './widgets/EmployeeHomeWidgets.scss';

type ChecklistScheduleEvent = EmployeeChecklistItem;

const toOutstandingChecklistViewItem = (
	item: ChecklistScheduleEvent,
): OutstandingChecklistScheduleEvent => ({
	id: item.id,
	scheduledDate: item.scheduledDate,
	scheduledTime: item.scheduledTime,
	checklistTemplate: {
		id: item.checklistTemplate?.id,
		label: item.checklistTemplate?.label || 'Unknown Template',
		version: item.checklistTemplate?.version,
		signoffRequired: item.checklistTemplate?.signoffRequired,
		type: item.checklistTemplate?.type,
		category: item.checklistTemplate?.category,
		frequency: item.checklistTemplate?.frequency,
		tasks: item.checklistTemplate?.tasks,
	},
});

const getPriorityOrder = (priorityLabel?: string | null): number => {
	const label = (priorityLabel || '').toLowerCase();
	if (label === 'critical' || label === 'urgent') return 1;
	if (label === 'high') return 2;
	if (label === 'medium') return 3;
	if (label === 'low') return 4;
	return 5;
};

const sortBroadcastsByPriority = <T extends Record<string, unknown>>(messages: T[]): T[] =>
	[...messages].sort((a, b) => {
		const priorityA = a.priority as { label?: string; name?: string } | undefined;
		const priorityB = b.priority as { label?: string; name?: string } | undefined;
		const orderA = getPriorityOrder(priorityA?.label || priorityA?.name);
		const orderB = getPriorityOrder(priorityB?.label || priorityB?.name);
		if (orderA !== orderB) return orderA - orderB;

		const dateA = getBroadcastMessageDate(a);
		const dateB = getBroadcastMessageDate(b);
		if (dateA && dateB) return dateB.getTime() - dateA.getTime();
		return 0;
	});

const EmployeeHomePage = () => {
	const { hasModuleViewPermission } = usePermissionCheck();
	const { userOrg } = useWorkplaceUser();
	const { currentUserRole } = useUserData();
	const products = useMemo(() => userOrg?.products || [], [userOrg?.products]);
	const canViewHomePage = hasModuleViewPermission('Home');
	const workplaceId = Number(localStorage.getItem('workplace-id') || 1);
	const currentUserId = getCurrentUserId();
	const currentSecurityRoleId = currentUserRole?.id ?? null;
	const { data: daysData } = useQuery('days', () => getDays(), {
		staleTime: 300000,
	});
	const startOfWeekLabel = resolveStartOfWeekLabel(userOrg, daysData);

	const rolePermissions = useMemo(
		() => (currentUserRole?.securityRolePermission ?? []) as SecurityRolePermission[],
		[currentUserRole],
	);

	const widgets = useMemo(
		() => resolveEmployeeWidgetVisibility(rolePermissions, products),
		[rolePermissions, products],
	);

	const canViewBroadcastMy = hasModuleViewPermission('Broadcast - My');
	const canViewBroadcastAll = hasModuleViewPermission('Broadcast - All');
	const canViewBroadcastOrganization = hasModuleViewPermission('Broadcast - Organization');
	const canViewToDoAll = hasModuleViewPermission('To-Do - All');
	const canViewToDoMy = hasModuleViewPermission('To-Do - My');
	const canViewChecklistAll = hasModuleViewPermission('Checklist - All');
	const canViewChecklistMy = hasModuleViewPermission('Checklist - My');

	const needsAssignmentGroups = widgets.canViewChecklist || widgets.canViewTodos;

	const { userGroupIds, isLoading: groupsLoading } = useUserAssignmentGroupIds(
		workplaceId,
		needsAssignmentGroups,
	);

	const [loading, setLoading] = useState(true);
	const [myShifts, setMyShifts] = useState<Awaited<ReturnType<typeof fetchMyPublishedShifts>>>([]);
	const [myBroadcasts, setMyBroadcasts] = useState<Record<string, unknown>[]>([]);
	const [myChecklists, setMyChecklists] = useState<ChecklistScheduleEvent[]>([]);
	const [myTodos, setMyTodos] = useState<TodoTableRow[]>([]);
	const [selectedChecklistItem, setSelectedChecklistItem] =
		useState<OutstandingChecklistScheduleEvent | null>(null);
	const [showChecklistDetail, setShowChecklistDetail] = useState(false);
	const [checklistDetailLoading, setChecklistDetailLoading] = useState(false);

	const handleChecklistClick = async (item: ChecklistScheduleEvent): Promise<void> => {
		if (!item.checklistTemplate?.id) {
			setSelectedChecklistItem(toOutstandingChecklistViewItem(item));
			setShowChecklistDetail(true);
			return;
		}

		setChecklistDetailLoading(true);
		try {
			const detailedTemplate = await getChecklistTemplateById(item.checklistTemplate.id);
			setSelectedChecklistItem(
				toOutstandingChecklistViewItem({
					...item,
					checklistTemplate: {
						...item.checklistTemplate,
						...detailedTemplate,
						tasks: detailedTemplate.tasks || [],
					},
				}),
			);
			setShowChecklistDetail(true);
		} catch (error) {
			console.error('Error fetching checklist template:', error);
			setSelectedChecklistItem(toOutstandingChecklistViewItem(item));
			setShowChecklistDetail(true);
		} finally {
			setChecklistDetailLoading(false);
		}
	};

	const handleChecklistDetailBack = () => {
		setShowChecklistDetail(false);
		setSelectedChecklistItem(null);
	};

	useEffect(() => {
		if (needsAssignmentGroups && groupsLoading) {
			return;
		}

		const fetchData = async () => {
			try {
				const tasks: Promise<void>[] = [];

				if (widgets.canViewSchedule) {
					tasks.push(
						(async () => {
							const shifts = await fetchMyPublishedShifts(workplaceId, startOfWeekLabel);
							setMyShifts(shifts);
						})(),
					);
				}

				if (widgets.canViewBroadcasts) {
					tasks.push(
						(async () => {
							const response = await getBroadcastMessage({
								workplaceId,
								page: 1,
								itemsPerPage: 100,
							});
							const inbox = Array.isArray(response?.member)
								? response.member
								: Array.isArray(response)
									? response
									: [];
							const scopedInbox = filterBroadcastInboxForUser(inbox, {
								currentUserId,
								canViewBroadcastMy,
								canViewBroadcastAll,
								canViewBroadcastOrganization,
							}) as Record<string, unknown>[];
							const unreadAllTime = sortBroadcastsByPriority(
								scopedInbox.filter((message) => message.isRead === false),
							);
							setMyBroadcasts(unreadAllTime);
						})(),
					);
				}

				if (widgets.canViewChecklist) {
					tasks.push(
						(async () => {
							const outstandingChecklistsRaw = await getMyOutstandingChecklists(workplaceId);
							const scopedOutstandingChecklists = canViewChecklistAll
								? outstandingChecklistsRaw
								: canViewChecklistMy
									? outstandingChecklistsRaw.filter((item) =>
											isChecklistEventAssignedToCurrentUser(
												item,
												currentUserId,
												userGroupIds,
												currentSecurityRoleId,
											),
										)
									: [];
							setMyChecklists(
								sortChecklistEventsByScheduledDateAsc(
									scopedOutstandingChecklists as ChecklistScheduleEvent[],
								),
							);
						})(),
					);
				}

				if (widgets.canViewTodos) {
					tasks.push(
						(async () => {
							const outstandingTodosRaw = await getMyOutstandingTodos(workplaceId);
							const scopedOutstandingTodos = canViewToDoAll
								? outstandingTodosRaw
								: canViewToDoMy
									? filterTodosForMyScope(
											outstandingTodosRaw,
											currentUserId,
											userGroupIds,
											currentSecurityRoleId,
										)
									: [];
							const outstandingTodos = normalizeAndSortTodos(scopedOutstandingTodos);

							setMyTodos(outstandingTodos);
						})(),
					);
				}

				await Promise.all(tasks);
			} catch (error) {
				console.error('Error fetching employee home data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [
		workplaceId,
		widgets,
		rolePermissions,
		currentUserId,
		currentUserRole,
		currentSecurityRoleId,
		userGroupIds,
		startOfWeekLabel,
		needsAssignmentGroups,
		groupsLoading,
		canViewBroadcastMy,
		canViewBroadcastAll,
		canViewBroadcastOrganization,
		canViewToDoAll,
		canViewToDoMy,
		canViewChecklistAll,
		canViewChecklistMy,
	]);

	const visibleWidgetCount = [
		widgets.canViewSchedule,
		widgets.canViewBroadcasts,
		widgets.canViewChecklist,
		widgets.canViewTodos,
	].filter(Boolean).length;

	if (!canViewHomePage) {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '400px',
					p: 3,
				}}
			>
				<Typography variant="h6" color="textSecondary">
					You do not have permission to view Home.
				</Typography>
			</Box>
		);
	}

	if (loading && visibleWidgetCount === 0) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (showChecklistDetail && selectedChecklistItem) {
		return (
			<div className="dashboard-container">
				<Paper elevation={3} sx={{ p: 2, backgroundColor: 'whitesmoke' }}>
					<Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
						<Button
							variant="outlined"
							onClick={handleChecklistDetailBack}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: 'white',
								padding: '5px 12px',
								'&:hover': { backgroundColor: 'rgb(56, 82, 114)' },
							}}
						>
							Back
						</Button>
					</Box>
					{checklistDetailLoading ? (
						<Box display="flex" justifyContent="center" p={4}>
							<CircularProgress size={24} />
						</Box>
					) : (
						<OutstandingChecklistView selectedItem={selectedChecklistItem} />
					)}
				</Paper>
			</div>
		);
	}

	return (
		<div className="dashboard-container">
			<Grid container spacing={2} className="employee-home-grid">
				{widgets.canViewSchedule && (
					<Grid
						item
						xs={12}
						md={visibleWidgetCount > 1 ? 6 : 12}
						className="employee-home-grid-item"
					>
						<EmployeeScheduleWidget loading={loading} shifts={myShifts} />
					</Grid>
				)}

				{widgets.canViewBroadcasts && (
					<Grid
						item
						xs={12}
						md={visibleWidgetCount > 1 ? 6 : 12}
						className="employee-home-grid-item"
					>
						<EmployeeBroadcastWidget
							loading={loading}
							items={myBroadcasts as never[]}
							workplaceId={workplaceId}
						/>
					</Grid>
				)}

				{widgets.canViewChecklist && (
					<Grid
						item
						xs={12}
						md={visibleWidgetCount > 2 ? 6 : 12}
						className="employee-home-grid-item"
					>
						<EmployeeChecklistWidget
							loading={loading}
							items={myChecklists}
							onItemClick={handleChecklistClick}
						/>
					</Grid>
				)}

				{widgets.canViewTodos && (
					<Grid
						item
						xs={12}
						md={visibleWidgetCount > 2 ? 6 : 12}
						className="employee-home-grid-item"
					>
						<EmployeeTodoWidget loading={loading} items={myTodos} />
					</Grid>
				)}
			</Grid>

			{!loading && visibleWidgetCount === 0 && (
				<Box sx={{ textAlign: 'center', py: 6 }}>
					<Typography variant="h6" color="textSecondary">
						No licensed modules are available for Home.
					</Typography>
				</Box>
			)}
		</div>
	);
};

export default EmployeeHomePage;
